import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { SearchInput, HorizonLine, useT } from '@repo/common';
import QnaSearchInput from '../Qna/QnaSearchInput';
import MobileSortFilter from '../Modal/MobileSortFilter';
import MobileFilterBottomSheet from './MobileFilterBottomSheet';

import { ReactComponent as FilterIcon } from '@repo/common/assets/icons/24px/icon_24px_filter.svg';
import { ReactComponent as CloseIcon } from '@repo/common/assets/icons/16px/icon_16px_cancel.svg';

import { getSearchQna } from '../../../api/qna/qnaSearch.api';

const FilterTag = ({ label, color, onRemove }) => (
  <div
    className={`flex items-center gap-2 py-1 pl-2 pr-1 rounded-full text-text-inverse typo-b12Strong ${color}`}
  >
    <span>{label}</span>
    <button
      onClick={onRemove}
      className='flex items-center justify-center w-[18px] h-[18px] rounded-full bg-grayscale-gray00 transition-colors'
    >
      <CloseIcon className='text-icon-secondary' />
    </button>
  </div>
);

const QnaSearchHeader = ({
  keyword: initialKeyword,
  filterState,
  setFilterState,
  activeCategory,
  setActiveCategory,
  appliedFilterState,
  setAppliedFilterState,
  onFilterApply,
}) => {
  const t = useT();
  const navigate = useNavigate();
  const searchInLabelsKr = {
    All: '전체',
    Questions: '질문',
    Answers: '답변',
  };
  const [isBottomSheetOpen, setIsBottomSheetOpen] = useState(false);
  const [keyword, setKeyword] = useState(initialKeyword || '');

  const handleSearch = async () => {
    if (!keyword.trim()) return;

    const result = await getSearchQna({
      keyword: keyword,
      page: 0,
    });

    navigate(`/community/qna/search?keyword=${encodeURIComponent(keyword)}`, {
      state: { searchResult: result },
    });
    window.location.reload();
  };

  const handleFilterChange = async (newState) => {
    setFilterState(newState);
    setAppliedFilterState(newState);

    if (!keyword.trim()) return;

    const result = await getSearchQna({
      keyword,
      sort: newState.sortBy,
      tutorOnly: newState.onlyTutor,
      page: 0,
    });

    onFilterApply({
      filters: newState,
      searchResult: result,
    });
  };

  const handleRemoveFilter = async (type, value) => {
    let newState = { ...appliedFilterState };

    if (type === 'searchIn') {
      newState.searchIn = 'All';
    } else if (type === 'onlyAnswered') {
      newState.onlyAnswered = false;
    } else if (type === 'category') {
      const targetIdx = newState.categoryNames?.indexOf(value);
      if (targetIdx > -1) {
        newState.categoryNames = newState.categoryNames.filter(
          (_, i) => i !== targetIdx,
        );
        newState.categoryIds = newState.categoryIds.filter(
          (_, i) => i !== targetIdx,
        );
      }
    }

    setFilterState(newState);
    setAppliedFilterState(newState);

    const result = await getSearchQna({
      keyword,
      sort: newState.sortBy,
      onlyAnswered: newState.onlyAnswered,
      searchIn: newState.searchIn,
      categoryIds: newState.categoryIds || [],
      page: 0,
    });

    onFilterApply({ filters: newState, searchResult: result });
  };

  return (
    <div className='flex justify-start tabletH:justify-center items-center tabletH:pb-20'>
      <div className='w-full tabletH:w-fit'>
        {/* 모바일 */}
        <div className='tabletH:hidden'>
          <div className='p-4'>
            <p
              className={`typo-b18Strong font-semibold tabletH:typo-h2 text-green-deep04 tabletH:text-text-primary 
              inline-block
              px-1 bg-[linear-gradient(transparent,transparent_50%,rgba(0,158,79,0.4)_50%)]
              tracking-wide tabletH:bg-none`}
            >
              Q&A
            </p>
          </div>

          <div className='w-full flex justify-center items-center gap-2 px-4 pb-6'>
            <div className='w-12 h-12 flex justify-center items-center'>
              <button
                onClick={() => setIsBottomSheetOpen(true)}
                className='flex justify-center items-center w-12 h-12 rounded-[8px] active:bg-functional-success01 active:text-green-deep03'
              >
                <FilterIcon />
              </button>
            </div>
            <SearchInput
              value={keyword}
              placeholder={t('what is your question?', '무엇이 궁금하신가요?')}
              activePlaceholder={t(
                'what is your question?',
                '무엇이 궁금하신가요?',
              )}
              cancelBg='bg-transform'
              cancelIcon='text-icon-primary'
              onChange={(v) => setKeyword(v)}
              onEnter={handleSearch}
            />
          </div>

          <div className='flex flex-wrap gap-[6px] px-4 pb-4'>
            {appliedFilterState.searchIn !== 'All' && (
              <FilterTag
                label={t(
                  appliedFilterState.searchIn,
                  searchInLabelsKr[appliedFilterState.searchIn] ??
                    appliedFilterState.searchIn,
                )}
                color='bg-green-deep03'
                onRemove={() => handleRemoveFilter('searchIn')}
              />
            )}
            {appliedFilterState.onlyAnswered && (
              <FilterTag
                label={t("Only with tutor's answer", '튜터 답변이 있는 질문만')}
                color='bg-orange-orange04'
                onRemove={() => handleRemoveFilter('onlyAnswered')}
              />
            )}
            {appliedFilterState.categoryNames?.map((name, idx) => (
              <FilterTag
                key={idx}
                label={name}
                color='bg-olive-olive04'
                onRemove={() => handleRemoveFilter('category', name)}
              />
            ))}
          </div>
        </div>

        <MobileFilterBottomSheet
          keyword={keyword}
          isOpen={isBottomSheetOpen}
          onClose={() => setIsBottomSheetOpen(false)}
          filterState={filterState}
          setFilterState={setFilterState}
          appliedFilterState={appliedFilterState}
          activeCategory={activeCategory}
          setActiveCategory={setActiveCategory}
          onApply={onFilterApply}
        />

        <HorizonLine className='tabletH:hidden' />

        <MobileSortFilter
          className='tabletH:hidden'
          isSticky={false}
          onChange={handleFilterChange}
          filterState={filterState}
          isSearchPage={true}
        />

        {/* 데스크탑 */}
        <div className='hidden tabletH:block pb-10 typo-h3'>
          <span className='text-text-primary'>
            {t('Q&A results for', 'Q&A 검색 결과')} “
          </span>
          <span
            className='text-green-deep04
              inline-block leading-none
              px-2
              bg-[linear-gradient(transparent,transparent_56%,rgba(0,158,79,0.4)_56%)]'
          >
            {keyword}
          </span>
          <span>”</span>
        </div>
        <div className='hidden tabletH:block tabletH:w-[570px]'>
          <QnaSearchInput defaultValue={keyword} />
        </div>
      </div>
    </div>
  );
};

export default QnaSearchHeader;
